import isEmpty from 'lodash/isEmpty';
import { getPropertyMap } from 'modules/journey/components/FilterInputsList';
import {
  SearchQuery,
  generateErrorObject,
  validateSearchQuery,
} from 'modules/workflowNodeEditors/components/StartNodeEditor/StartNodeEditor.hooks';
import { Query } from 'modules/journey/components/FilterInputsList/FilterInputsList.types';
import { getClientDataTypeFromEventType } from 'utils/dataTypes';
import {
  CAMPAIGN,
  LIST_MEMBERSHIP,
  LIST_MEMBERSHIP_SINGLE,
  MESSAGE_CHANNEL,
  MESSAGE_TYPE,
  UNSUBSCRIBED_CHANNEL,
  UNSUBSCRIBED_CHANNELS,
  UNSUBSCRIBED_MESSAGE_TYPE,
  UNSUBSCRIBED_MESSAGE_TYPES,
  USER_LIST_MEMBERSHIP,
  USER_LIST_MEMBERSHIP_SINGLE,
} from 'utils/fieldConstants';
import { RULE_TYPES } from '../constants';
import {
  ChildRule,
  ChildRuleValidationErrors,
  ContainsCloningError,
  EventAndSearchQueryValidationErrors,
  Rule,
  RuleValidationErrors,
  Rules,
  SearchQueriesValidationErrors,
} from '../types';

const FILTER_SELECT_TYPES = [
  LIST_MEMBERSHIP,
  CAMPAIGN,
  MESSAGE_CHANNEL,
  MESSAGE_TYPE,
  USER_LIST_MEMBERSHIP,
  UNSUBSCRIBED_CHANNELS,
  UNSUBSCRIBED_MESSAGE_TYPES,
  LIST_MEMBERSHIP_SINGLE,
  USER_LIST_MEMBERSHIP_SINGLE,
  UNSUBSCRIBED_CHANNEL,
  UNSUBSCRIBED_MESSAGE_TYPE,
];

const uppercaseFirstLetter = (text: string) => {
  return text[0].toUpperCase() + text.slice(1);
};
const parseQuery = (
  searchQuery: Partial<Query> & ContainsCloningError & { valueDisplay?: string },
  mappings: any = {},
) => {
  const { field = '', dataType = '', value, comparatorType, valueDisplay = '' } = searchQuery;
  const eventType = getClientDataTypeFromEventType(uppercaseFirstLetter(dataType));
  const correctDataType = eventType || dataType;
  const isCustomEventType = dataType === 'customEvent';
  const propertyMap = getPropertyMap({ mappings, dataType: correctDataType, isUUIDProject: false });
  const isValidField = !!propertyMap[field];
  const isValidValue =
    (isCustomEventType ? !!propertyMap[field] : !FILTER_SELECT_TYPES?.includes(field)) && !!value;
  return {
    ...searchQuery,
    // strip values that can't be copied from the clone
    field: isValidField ? field : null,
    comparatorType: isValidField ? comparatorType : null,
    value: isValidValue ? value : null,
    // values to display as placeholders
    fieldDisplay: field,
    comparatorTypeDisplay: comparatorType,
    valueDisplay: value || valueDisplay,
  };
};

export const validateClonedRules = ({
  rules = [],
  allEvents = [],
  mappings = {},
}: {
  rules: Rules;
  allEvents: any;
  mappings: any;
}) => {
  // these functions need allEvents and mappings, which they can access from the closure
  const reduceSearchQueryErrors = (
    acc: SearchQueriesValidationErrors,
    searchQuery: Partial<Query> & ContainsCloningError,
    idx: number,
  ) => {
    const { containsCloningError = false } = searchQuery;
    const query = containsCloningError ? parseQuery(searchQuery, mappings) : searchQuery;
    const searchQueryValidations = validateSearchQuery(query as SearchQuery);
    if (!isEmpty(searchQueryValidations)) {
      acc[idx] = searchQueryValidations;
    }
    return acc;
  };

  const validateChildRule = (childRule: ChildRule) => {
    const { searchQuery, ruleType, eventName } = childRule;
    const childRuleErrorObject = {} as EventAndSearchQueryValidationErrors;
    const isPropertyRule = ruleType === RULE_TYPES?.USER_PROPERTY;
    const isInvalid =
      allEvents?.find((event: any) => event?.field === childRule?.eventName) === undefined;
    const { searchQueries } = searchQuery;
    if (!searchQueries) return childRuleErrorObject;
    const searchQueriesErrors = searchQueries.reduce(reduceSearchQueryErrors, {});

    if (!isEmpty(searchQueriesErrors)) {
      childRuleErrorObject.searchQuery = { searchQueries: searchQueriesErrors };
    }

    if (!isPropertyRule && eventName && isInvalid) {
      childRuleErrorObject.eventName = generateErrorObject(eventName);
    }

    return isEmpty(childRuleErrorObject) ? {} : childRuleErrorObject;
  };

  const reduceChildRuleErrors = (
    acc: ChildRuleValidationErrors,
    childRule: ChildRule,
    idx: number,
  ) => {
    const validatedChildRule = validateChildRule(childRule);
    if (!isEmpty(validatedChildRule)) {
      acc[idx] = validatedChildRule;
    }
    return acc;
  };

  const validateRule = (rule: Rule) => {
    const childRuleErrors = rule?.childRules?.reduce(reduceChildRuleErrors, {});
    return childRuleErrors;
  };

  const reduceRuleErrors = (acc: RuleValidationErrors, rule: Rule, idx: number) => {
    const validatedRules = validateRule(rule);
    if (!isEmpty(validatedRules)) {
      acc[idx] = validatedRules;
    }
    return acc;
  };

  const clonedRulesErrors = rules.reduce(reduceRuleErrors, {});
  return isEmpty(clonedRulesErrors) ? {} : { rules: clonedRulesErrors };
};
